/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import StateInterface from "../../../../../redux-magic/state-interface";

/* Component Imports */

import { CircularProgress, Skeleton, Typography, useTheme } from "@mui/material";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";

/* Icon Imports */

import { CheckCircleOutlined, RemoveCircleOutlined } from "@mui/icons-material";
import { ObjectId } from "bson";
import { removeReceivedBroadcastFromListThunk, setCurrentChatThunk } from "../../../../../redux-magic/store";
import { ChatInterface } from "../../../../../redux-magic/sub-interfaces/chat-interface";

/* Styled Components */

const MainContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	gap: "0.25rem",
	padding: "0rem 0.75rem 0rem 0.75rem",
	width: "100%",
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	gap: "0.25rem",
}));

const CardCTA = ({
	session,
	dispatch,
	loading,
	router,
	profile_context,
	invite_status,
	broadcast_id,
	sender_name,
	sender_is_business_profile,
	sender_user_id,
	sender_business_profile_id,
	chat_dp,
	property_name,
	property_id,
	broadcast_notes,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	loading: boolean;
	router: NextRouter;
	profile_context: StateInterface["profile_context"];
	invite_status: string;
	broadcast_id: ObjectId | string;
	sender_name: string;
	sender_is_business_profile: boolean;
	sender_user_id: ObjectId | string;
	sender_business_profile_id: ObjectId | string;
	chat_dp: string;
	property_name: string;
	property_id: ObjectId | string;
	broadcast_notes: string;
}) => {
	const theme = useTheme();

	/* Translations */

	const localLang = {
		accept: router.locale === "ar-AE" ? "قبول" : router.locale === "kn-IN" ? "ಸ್ವೀಕರಿಸಿ" : "Accept",
		// The Kannada word used to mean "mock" (chat audit I12).
		ignore: router.locale === "ar-AE" ? "تجاهل" : router.locale === "kn-IN" ? "ನಿರ್ಲಕ್ಷಿಸಿ" : "Ignore",
		request_accepted:
			router.locale === "ar-AE"
				? "تم قبول الطلب"
				: router.locale === "kn-IN"
					? "ಕೋರಿಕೆ ಒಪ್ಪಿಗೆಯಾಗಿದೆ"
					: "Request accepted",
		request_ignored:
			router.locale === "ar-AE"
				? "تم تجاهل الطلب"
				: router.locale === "kn-IN"
					? "ಕೋರಿಕೆ ನಿರ್ಲಕ್ಷಿಸಲಾಗಿದೆ"
					: "Request ignored",
		go_to_chat:
			router.locale === "ar-AE" ? "اذهب إلى الدردشة" : router.locale === "kn-IN" ? "ಚಾಟ್‌ಗೆ ಹೋಗಿ" : "Go to chat",
	};

	const [ignoreButtonLoading, setIgnoreButtonLoading] = React.useState(false);
	const [acceptButtonLoading, setAcceptButtonLoading] = React.useState(false);

	const [inviteStatus, setInviteStatus] = React.useState(invite_status || "pending");

	const [chatData, setChatData] = React.useState<ChatInterface | null>(null);

	/* Function to handle click on Accept or Ignore button */

	const handleClickRespondInvite = (invite_status: string) => {
		if (invite_status === "accepted") {
			setAcceptButtonLoading(true);
			setIgnoreButtonLoading(false);
		} else if (invite_status === "ignored") {
			setIgnoreButtonLoading(true);
			setAcceptButtonLoading(false);
		}

		fetch(`${process.env.PRODUCTION_API_URL}broadcast/update-broadcast-invite-status`, {
			method: "POST",
			cache: "no-store",
			body: JSON.stringify({
				invite_status: invite_status,
				broadcast_id: broadcast_id,
				broadcast_notes: broadcast_notes,
				chat_title: sender_name + ": " + property_name,
				sender_is_business_profile: sender_is_business_profile,
				sender_user_id: sender_user_id,
				sender_business_profile_id: sender_business_profile_id,
				responder_is_business_profile: profile_context.is_business_profile,
				responder_user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
				responder_business_profile_id: profile_context.business_profile_id,
				property_id: property_id,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
			}),
		})
			.then((res) => res.json())
			.then((data) => {
				if (data.status === "success") {
					if (invite_status === "ignored") {
						dispatch(
							removeReceivedBroadcastFromListThunk({
								broadcast_id: broadcast_id,
								invite_status: invite_status,
							}),
						);
					}
					setChatData(data.payload.chat);
					setInviteStatus(invite_status);
				}
				setIgnoreButtonLoading(false);
				setAcceptButtonLoading(false);
			})
			.catch((error) => {
				console.error("Error responding to invite:", error);
				setIgnoreButtonLoading(false);
				setAcceptButtonLoading(false);
			});
	};

	/* Function to handle click on Go to chat button */

	/* "Go to chat" (chat audit I12, 17 Oct 2026). The conversation came back only from an accept made
	   in this visit, so after a reload the button did nothing. With no chat in hand it now asks again:
	   answering "accepted" a second time writes nothing (the server keeps the first answer) and returns
	   the pair's conversation, which is then opened. */
	const handleClickOpenChat = async () => {
		let chatToOpen: ChatInterface | null = chatData;
		if (!chatToOpen) {
			try {
				const response = await fetch(`${process.env.PRODUCTION_API_URL}broadcast/update-broadcast-invite-status`, {
					method: "POST",
					cache: "no-store",
					body: JSON.stringify({
						invite_status: "accepted",
						broadcast_id: broadcast_id,
						responder_is_business_profile: profile_context.is_business_profile,
						responder_business_profile_id: profile_context.business_profile_id,
					}),
				});
				const data = await response.json();
				if (data && data.status === "success" && data.payload?.chat) {
					chatToOpen = data.payload.chat;
					setChatData(data.payload.chat);
				}
			} catch {
				// Nothing to open; the button stays as it is.
			}
		}
		if (chatToOpen) {
			// The chat to open, under the name the code below already reads.
			const chatData: ChatInterface = chatToOpen;
			dispatch(
				setCurrentChatThunk({
					chat_id: chatData ? chatData._id : "",
					chat_type: chatData ? chatData.chat_type : "broadcast",
					chat_title: chatData ? chatData.chat_title : "",
					chat_description: chatData ? chatData.chat_description : "",
					chat_dp: {
						file_id: chat_dp,
						exists: chat_dp ? true : false,
						original_file_server_path: "",
					},
					chat_is_broadcast: true,
					chat_notification_settings: {
						is_business_profile: profile_context.is_business_profile,
						user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
						business_profile_id: profile_context.business_profile_id,
						is_muted: false,
						muted_till: "",
					},
					chat_participants: chatData ? chatData.participants : [],
					chat_created_at: chatData ? chatData.created_at : "",
					chat_source_collection: "broadcasts",
					// chat_last_message: null,
					chat_broadcast_id: broadcast_id,
					property_id: property_id,
					
				}),
			);

			dispatch(
				removeReceivedBroadcastFromListThunk({
					broadcast_id: broadcast_id,
					invite_status: inviteStatus,
				}),
			);
		}
	};

	if (loading) {
		return (
			<Skeleton
				animation="wave"
				variant="text"
				width="75%"
				height="auto"
				style={{ marginBottom: "0.5rem" }}
			/>
		);
	}

	return (
		<MainContainer>
			{inviteStatus === "accepted" ? (
				<RowContainer
					sx={{
						width: "100%",
					}}
				>
					<RowContainer>
						<CheckCircleOutlined
							sx={{
								color: theme.palette.success.main,
							}}
						/>

						<Typography
							variant="body2"
							sx={{
								color: theme.palette.text.secondary,
							}}
						>
							{localLang.request_accepted}
						</Typography>
					</RowContainer>

					{/* Go to chat Button  */}

					<BeegruButton
						flavor="info"
						variant="text"
						disableTouchRipple
						onClick={(event: any) => {
							event.preventDefault();
							handleClickOpenChat();
						}}
					>
						{localLang.go_to_chat}
					</BeegruButton>
				</RowContainer>
			) : inviteStatus === "ignored" ? (
				/* A decline is never final (decided 16 Oct 2026): an ignored offer can still be accepted. */
				<RowContainer
					sx={{
						width: "100%",
					}}
				>
					<RowContainer>
						<RemoveCircleOutlined
							sx={{
								color: theme.palette.error.main,
							}}
						/>

						<Typography
							variant="body2"
							sx={{
								color: theme.palette.text.secondary,
							}}
						>
							{localLang.request_ignored}
						</Typography>
					</RowContainer>

					{/* Accept Button */}

					<BeegruButton
						flavor="success"
						variant="contained"
						size="medium"
						disabled={acceptButtonLoading}
						onClick={(event: any) => {
							event.preventDefault();
							handleClickRespondInvite("accepted");
						}}
					>
						{acceptButtonLoading ? (
							<CircularProgress
								sx={{
									color: theme.palette.text.disabled,
								}}
							/>
						) : (
							localLang.accept
						)}
					</BeegruButton>
				</RowContainer>
			) : (
				<RowContainer
					sx={{
						width: "100%",
					}}
				>
					{/* Ignore Button */}

					<BeegruButton
						flavor="error"
						variant="text"
						size="medium"
						fullWidth
						disabled={ignoreButtonLoading || acceptButtonLoading}
						onClick={(event: any) => {
							event.preventDefault();
							handleClickRespondInvite("ignored");
						}}
					>
						{ignoreButtonLoading ? (
							<CircularProgress
								sx={{
									color: theme.palette.text.disabled,
								}}
							/>
						) : (
							localLang.ignore
						)}
					</BeegruButton>

					{/* Accept Button */}

					<BeegruButton
						flavor="success"
						variant="contained"
						size="medium"
						fullWidth
						disabled={ignoreButtonLoading || acceptButtonLoading}
						onClick={(event: any) => {
							event.preventDefault();
							handleClickRespondInvite("accepted");
						}}
					>
						{acceptButtonLoading ? (
							<CircularProgress
								sx={{
									color: theme.palette.text.disabled,
								}}
							/>
						) : (
							localLang.accept
						)}
					</BeegruButton>
				</RowContainer>
			)}
		</MainContainer>
	);
};

export default CardCTA;
